import { useEffect, useState } from 'react'
import { PublicClientApplication } from '@azure/msal-browser'
import { GoogleLogin } from '@react-oauth/google'
import { Link, useLocation, useNavigate } from 'react-router'

import {
  appSignedInRoutePath,
  passwordResetRoutePath,
  registerRoutePath,
  resetPasswordConfirmationRoutePath,
  signInRoutePath,
} from 'config/routePaths'
import logo from 'Images/logo.png'
import { useEmailPasswordAuth, usePasswordChange, usePasswordReset, useSignIn } from 'query/useAuth'
import { Alert, Button, Field, Form, Input } from 'ui'
import { getReturnTo, safeReturnTo, withReturnTo } from 'utils/returnTo'
import { setSessionData } from 'utils/session'
import { isValidEmail } from 'utils/validation'

import OAuthButton from './OAuthButton'

const microsoftPopupRequest = { prompt: 'select_account', scopes: ['user.read'] }
let microsoftClient

export const handleSignInResponse = (response, {
  navigate,
  redirect = value => { window.location.href = value },
  returnTo,
  setError,
  storeSession = setSessionData,
}) => {
  if (response.key) {
    redirect(`${appSignedInRoutePath}?key=${response.key}`)
    return
  }

  if (response.jwt_token) {
    storeSession(response.jwt_token)
    redirect(safeReturnTo(returnTo) || '/')
    return
  }

  if (response.redirect_to) {
    try {
      const redirectUrl = new URL(response.redirect_to, window.location.origin)
      navigate(redirectUrl.origin === window.location.origin
        ? `${redirectUrl.pathname}${redirectUrl.search}${redirectUrl.hash}`
        : '/')
    } catch (_error) {
      navigate('/')
    }

    return
  }

  setError(response.status || 'Sign-in failed.')
}

const PETAL_ANGLES = [0, 72, 144, 216, 288]
const BLOOM_ROTATIONS = [0, -13, 9, -21, 17, -7, 23, -17, 5, -25, 13]

const Petals = ({ stroke }) => (
  <g fill={stroke ? 'none' : 'currentColor'} stroke={stroke} strokeWidth={stroke ? 1 : undefined}>
    {PETAL_ANGLES.map(angle => (
      <ellipse
        cx="0"
        cy="-34"
        key={angle}
        rx="33"
        ry="45"
        transform={`rotate(${angle}) rotate(16 0 -34)`}
        vectorEffect={stroke ? 'non-scaling-stroke' : undefined}
      />
    ))}
  </g>
)

const BloomArt = () => (
  <div aria-hidden="true" className="ds-auth-bloom">
    <svg height="0" style={{ position: 'absolute' }} width="0">
      <defs>
        <linearGradient id="ds-auth-bloom-a" x1="0" x2="1" y1="0" y2="1">
          <stop className="ds-auth-bloom-stop-0" offset="0" />
          <stop className="ds-auth-bloom-stop-1" offset="0.55" />
          <stop className="ds-auth-bloom-stop-2" offset="1" />
        </linearGradient>
        <linearGradient id="ds-auth-bloom-b" x1="1" x2="0" y1="0" y2="1">
          <stop className="ds-auth-bloom-stop-1" offset="0" />
          <stop className="ds-auth-bloom-stop-2" offset="0.6" />
          <stop className="ds-auth-bloom-stop-0" offset="1" />
        </linearGradient>
        <linearGradient id="ds-auth-bloom-c" x1="0" x2="1" y1="1" y2="0">
          <stop className="ds-auth-bloom-stop-2" offset="0" />
          <stop className="ds-auth-bloom-stop-0" offset="0.5" />
          <stop className="ds-auth-bloom-stop-1" offset="1" />
        </linearGradient>
      </defs>
    </svg>
    {BLOOM_ROTATIONS.map((rotation, index) => (
      <div
        className="ds-auth-bloom-ring"
        key={rotation + index}
        style={{
          '--ds-auth-bloom-opacity': (0.68 - index * 0.04).toFixed(2),
          '--ds-auth-bloom-rotation': `${rotation}deg`,
          '--ds-auth-bloom-width': `${Math.round(300 * Math.pow(1.145, index))}px`,
        }}
      >
        <svg viewBox="-100 -100 200 200">
          <Petals stroke={`url(#ds-auth-bloom-${['a', 'b', 'c'][index % 3]})`} />
        </svg>
      </div>
    ))}
  </div>
)

const AuthMark = () => <img alt="ExpanDrive" className="ds-auth-mark" src={logo} />

// Purchase intent stays in the opener. Microsoft requires a registered
// callback URL, without the page's return_to query or fragment.
const getMicrosoftRedirectUri = () => `${window.location.origin}${window.location.pathname}`

const getMicrosoftClient = () => {
  if (!microsoftClient) {
    microsoftClient = new PublicClientApplication({
      auth: {
        authority: 'https://login.microsoftonline.com/common',
        clientId: process.env.MICROSOFT_CLIENT_ID,
        redirectUri: getMicrosoftRedirectUri(),
      },
      cache: { cacheLocation: 'sessionStorage' },
    })
  }

  return microsoftClient
}

const GoogleMark = () => (
  <svg aria-hidden="true" height="18" viewBox="0 0 48 48" width="18">
    <path d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" fill="#EA4335" />
    <path d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" fill="#4285F4" />
    <path d="M10.53 28.59A13.8 13.8 0 0 1 9.77 24c0-1.6.27-3.14.76-4.59l-7.98-6.19A24 24 0 0 0 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" fill="#FBBC05" />
    <path d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" fill="#34A853" />
  </svg>
)

const MicrosoftMark = () => (
  <svg aria-hidden="true" height="18" viewBox="0 0 19 19" width="18">
    <rect fill="#f25022" height="9" width="9" />
    <rect fill="#00a4ef" height="9" width="9" y="10" />
    <rect fill="#7fba00" height="9" width="9" x="10" />
    <rect fill="#ffb900" height="9" width="9" x="10" y="10" />
  </svg>
)

const Separator = () => (
  <div className="tw:my-5 tw:flex tw:items-center tw:gap-3 tw:text-sm tw:text-foreground-subtle">
    <span className="tw:h-px tw:flex-1 tw:bg-border" />
    or
    <span className="tw:h-px tw:flex-1 tw:bg-border" />
  </div>
)

const EmailPasswordForm = ({ isRegister, hasExpandriveAppParam, returnTo }) => {
  const location = useLocation()
  const navigate = useNavigate()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)
  const { mutateAsync: submitAuth } = useEmailPasswordAuth(isRegister)

  useEffect(() => {
    setEmail('')
    setPassword('')
    setError('')
  }, [location.pathname])

  useEffect(() => {
    if (error) setError('')
  }, [email, password])

  const handleSubmit = async event => {
    event?.preventDefault?.()
    if (busy) return

    const payload = {
      email: email.trim(),
      password: password.trim(),
      ...(hasExpandriveAppParam && { app_sign_in: true }),
    }

    if (!payload.email) return setError('Email is required.')
    if (!payload.password) return setError('Password is required.')

    setBusy(true)
    setError('')
    try {
      const data = await submitAuth({
        payload,
        requestOptions: {
          onFailure: (response, json) => {
            setBusy(false)
            setError(json.status || response.statusText)
          },
        },
      })

      if (data.key) {
        window.parent.postMessage(`key=${data.key}`, '*')
      }

      handleSignInResponse(data, { navigate, returnTo, setError: message => {
        setBusy(false)
        setError(message)
      } })
    } catch (apiError) {
      setBusy(false)
      setError(apiError.message || 'Unable to authenticate.')
    }
  }

  return (
    <Form onSubmit={handleSubmit}>
      {error && <Alert color="danger">{error}</Alert>}
      <Field label="Email">
        <Input autoComplete={isRegister ? 'email' : 'username'} autoFocus disabled={busy} name="email" onChange={event => setEmail(event.currentTarget.value)} required type="email" value={email} />
      </Field>
      <Field label="Password">
        <Input autoComplete={isRegister ? 'new-password' : 'current-password'} disabled={busy} name="password" onChange={event => setPassword(event.currentTarget.value)} required type="password" value={password} />
      </Field>
      <Button className="tw:mt-1 tw:w-full" disabled={busy} size="lg" type="submit">
        {busy ? 'Working…' : isRegister ? 'Register' : 'Sign In'}
      </Button>
      {!isRegister && (
        <div className="tw:mt-4 tw:text-center">
          <Link className="tw:text-sm tw:hover:underline" to={withReturnTo(passwordResetRoutePath, returnTo)}>Reset password</Link>
        </div>
      )}
    </Form>
  )
}

const PasswordResetForm = ({ returnTo }) => {
  const [email, setEmail] = useState('')
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)
  const [didSend, setDidSend] = useState(false)
  const { mutateAsync } = usePasswordReset()

  const handleSubmit = async event => {
    event.preventDefault()
    const value = email.trim()
    if (!value) {
      setDidSend(false)
      return setError('Email is required.')
    }

    if (!isValidEmail(value)) {
      setDidSend(false)
      return setError('Email is not valid.')
    }

    setBusy(true)
    setError('')
    setDidSend(false)
    try {
      const data = await mutateAsync({ payload: { email: value } })
      setDidSend(data.status === 'sent')
      setBusy(false)
    } catch (apiError) {
      setBusy(false)
      setDidSend(false)
      setError(apiError.message || 'Unable to request password reset.')
    }
  }

  return (
    <Form onSubmit={handleSubmit}>
      {error && <Alert color="danger">{error}</Alert>}
      {didSend && <Alert color="success">If the account exists, you will receive password reset instructions.</Alert>}
      <Field label="Email">
        <Input autoFocus disabled={busy} onChange={event => setEmail(event.currentTarget.value)} type="email" value={email} />
      </Field>
      <Button className="tw:w-full" disabled={busy} size="lg" type="submit">{busy ? 'Sending…' : 'Request Password Reset'}</Button>
      <div className="tw:mt-4 tw:text-center"><Link className="tw:text-sm tw:hover:underline" to={withReturnTo(signInRoutePath, returnTo)}>Back to sign in</Link></div>
    </Form>
  )
}

const ResetPasswordConfirmationForm = ({ returnTo }) => {
  const location = useLocation()
  const token = new URLSearchParams(location.search).get('reset_password_token')
  const [password, setPassword] = useState('')
  const [confirmation, setConfirmation] = useState('')
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)
  const { mutateAsync } = usePasswordChange()

  if (!token) {
    window.location.href = withReturnTo(signInRoutePath, returnTo)
    return null
  }

  const handleSubmit = async event => {
    event.preventDefault()
    if (!password) return setError('Password is required.')
    if (!confirmation) return setError('Confirm password is required.')
    if (password !== confirmation) return setError('Passwords do not match.')

    setBusy(true)
    setError('')
    try {
      await mutateAsync({
        payload: { password: password.trim(), password_confirmation: confirmation.trim(), reset_password_token: token },
      })

      window.location.href = withReturnTo(signInRoutePath, returnTo)
    } catch (apiError) {
      setBusy(false)
      setError(apiError.message || 'Unable to reset password.')
    }
  }

  return (
    <Form onSubmit={handleSubmit}>
      {error && <Alert color="danger">{error}</Alert>}
      <Field helper="At least 6 characters." label="New Password">
        <Input autoComplete="new-password" autoFocus disabled={busy} minLength={6} onChange={event => setPassword(event.currentTarget.value)} type="password" value={password} />
      </Field>
      <Field label="Confirm Password">
        <Input autoComplete="new-password" disabled={busy} minLength={6} onChange={event => setConfirmation(event.currentTarget.value)} type="password" value={confirmation} />
      </Field>
      <Button className="tw:w-full" disabled={busy} size="lg" type="submit">{busy ? 'Saving…' : 'Set New Password'}</Button>
    </Form>
  )
}

const Login = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const params = new URLSearchParams(location.search)
  const hasExpandriveAppParam = params.get('expandrive_app') === 'true'
  const isAppSignIn = hasExpandriveAppParam || navigator.userAgent.includes('ExpanDrive') || navigator.userAgent.includes('Electron/')
  const returnTo = isAppSignIn ? null : getReturnTo(location.search)
  const [ssoError, setSsoError] = useState('')
  const { mutate: signIn } = useSignIn({
    onError: error => setSsoError(error.message || 'Sign-in failed.'),
    onSuccess: response => handleSignInResponse(response, { navigate, returnTo, setError: setSsoError }),
  })

  const loginWithToken = options => signIn({ user: {}, ...options })
  const signInWithMicrosoft = async () => {
    try {
      const client = getMicrosoftClient()
      if (typeof client.initialize === 'function') await client.initialize()
      const response = await client.loginPopup({ ...microsoftPopupRequest, redirectUri: getMicrosoftRedirectUri() })
      if (response?.accessToken) loginWithToken({ microsoft_token: response.accessToken })
      else setSsoError('Microsoft sign-in did not return an access token.')
    } catch (error) {
      setSsoError(error?.message || 'Microsoft sign-in failed.')
    }
  }

  const isRegister = location.pathname === registerRoutePath
  const isPasswordReset = location.pathname === passwordResetRoutePath
  const isResetConfirmation = location.pathname === resetPasswordConfirmationRoutePath
  const title = isRegister ? 'Create Your Account' : isPasswordReset ? 'Reset Your Password' : isResetConfirmation ? 'Choose a New Password' : 'Sign In to Your Account'
  const subtitle = isRegister
    ? 'Set up access for your organization.'
    : isPasswordReset
      ? 'Enter your email to receive reset instructions.'
      : isResetConfirmation
        ? 'Choose a secure password for your account.'
        : 'Manage drive configurations, seats, and devices.'

  const providerAction = isRegister ? 'Sign up' : 'Sign in'

  return (
    <main className="ds-auth-shell">
      <BloomArt />
      <section className="ds-auth-plate">
        <AuthMark />
        <h1 className="ds-heading-2 tw:text-center">{title}</h1>
        <p className="tw:mt-2 tw:text-center tw:text-base tw:text-foreground-muted">{subtitle}</p>

        <div className="tw:mt-6">
          {isPasswordReset
            ? <PasswordResetForm returnTo={returnTo} />
            : isResetConfirmation
              ? <ResetPasswordConfirmationForm returnTo={returnTo} />
              : <EmailPasswordForm hasExpandriveAppParam={hasExpandriveAppParam} isRegister={isRegister} returnTo={returnTo} />}
        </div>

        {!isPasswordReset && !isResetConfirmation && (
          <>
            <Separator />
            {ssoError && <Alert color="danger">{ssoError}</Alert>}
            <div className="tw:grid tw:grid-cols-1 tw:justify-items-center tw:gap-3">
              {isAppSignIn ? (
                <>
                  <OAuthButton icon={<GoogleMark />} provider="google_oauth2">{providerAction} with Google</OAuthButton>
                  <OAuthButton icon={<MicrosoftMark />} provider="microsoft_office365">{providerAction} with Microsoft</OAuthButton>
                </>
              ) : (
                <>
                  <div className="ds-auth-google-button tw:flex tw:min-h-10 tw:items-center tw:justify-center">
                    <GoogleLogin
                      containerProps={{ className: 'ds-auth-google-control' }}
                      onError={() => setSsoError('Google sign-in failed.')}
                      onSuccess={response => response.credential && loginWithToken({ google_token: response.credential })}
                      shape="pill"
                      size="large"
                      text={isRegister ? 'signup_with' : 'signin_with'}
                      theme="outline"
                      ux_mode="popup"
                    />
                  </div>
                  <Button className="ds-auth-provider-button" icon={<MicrosoftMark />} onClick={signInWithMicrosoft} variant="secondary">{providerAction} with Microsoft</Button>
                </>
              )}
            </div>
            <div className="tw:mt-6 tw:text-center tw:text-sm tw:text-foreground-muted">
              {isRegister ? <>Already have an account? <Link className="tw:font-semibold tw:text-foreground-strong tw:hover:underline" to={withReturnTo(signInRoutePath, returnTo)}>Sign in</Link></> : <>Don&apos;t have an account? <Link className="tw:font-semibold tw:text-foreground-strong tw:hover:underline" to={withReturnTo(registerRoutePath, returnTo)}>Register</Link></>}
            </div>
          </>
        )}
      </section>
    </main>
  )
}

export default Login
